import { CAMERA_MOTION_KEYFRAME_TIME_GAP, CAMERA_MOTION_MAX_KEYFRAMES } from './cameraMotionConfig';
import {
  type CameraMotionDuplicateSlot,
  type CameraMotionKeyframe,
  type CameraMotionPose,
  type CameraMotionTrajectoryKeyframe,
} from './cameraMotionTypes';

let keyframeSequence = 0;

export const createCameraMotionKeyframe = (pose: CameraMotionPose, time: number): CameraMotionKeyframe => {
  keyframeSequence += 1;

  return {
    azimuth: pose.azimuth,
    distance: pose.distance,
    elevation: pose.elevation,
    id: `camera-motion-${keyframeSequence}`,
    time,
  };
};

export const toCameraMotionTrajectory = (
  keyframes: readonly CameraMotionKeyframe[],
): CameraMotionTrajectoryKeyframe[] =>
  keyframes.map(({ azimuth, distance, elevation, time }) => ({ azimuth, distance, elevation, time }));

/** Times closer than this count as the same, so evenly spaced cameras still read as even after rounding. */
const CAMERA_MOTION_TIME_EPSILON = 0.001;

const evenCameraMotionTime = (index: number, count: number): number => (count > 1 ? index / (count - 1) : 0);

/** The same cameras and angles, spread evenly across the clip. */
export const spaceCameraMotionKeyframesEvenly = (keyframes: readonly CameraMotionKeyframe[]): CameraMotionKeyframe[] =>
  keyframes.map((keyframe, index) => ({ ...keyframe, time: evenCameraMotionTime(index, keyframes.length) }));

export const areCameraMotionKeyframesEvenlySpaced = (keyframes: readonly CameraMotionKeyframe[]): boolean =>
  keyframes.every(
    (keyframe, index) =>
      Math.abs(keyframe.time - evenCameraMotionTime(index, keyframes.length)) < CAMERA_MOTION_TIME_EPSILON,
  );

/**
 * Where a copy of the camera at `index` goes: just after it, or just before it for the end camera, which is pinned to
 * the end of the clip. Lands about one second away or halfway to the neighbor, whichever is closer, so the neighboring
 * move keeps some time. Null when every camera slot is used or the neighbor is too close.
 */
export const cameraMotionDuplicateSlot = (
  keyframes: readonly CameraMotionKeyframe[],
  index: number,
  duration: number,
): CameraMotionDuplicateSlot | null => {
  const current = keyframes[index];
  const isLast = index === keyframes.length - 1;
  const neighbor = keyframes[isLast ? index - 1 : index + 1];

  if (!current || !neighbor || keyframes.length >= CAMERA_MOTION_MAX_KEYFRAMES || duration <= 0) {
    return null;
  }

  const span = Math.abs(neighbor.time - current.time);

  if (span < CAMERA_MOTION_KEYFRAME_TIME_GAP * 2) {
    return null;
  }

  const offset = Math.min(1 / duration, span / 2);

  return isLast
    ? { insertAt: index, time: current.time - offset }
    : { insertAt: index + 1, time: current.time + offset };
};
